/**
 * PATH: packages/kanban-host/src/allocation/AllocationWhyPopover.tsx
 * FILE_ID: KANBANHOST-ALLOCATIONWHYPOPOVER-001
 * PURPOSE: Canonical body of this kanban-allocation module.
 *          Lifted from apps/<tenant>/src/features/kanban/allocation/AllocationWhyPopover.tsx
 *          (see docs/architecture/EMAIL_KANBAN_CUTOVER_ARC.md K4).
 * WHY:     Removes the previous tenant-side duplication of
 *          this byte-identical body. One source of truth, both
 *          tenants consume from this package via thin re-export
 *          shims at apps/<tenant>/src/features/kanban/<rel>.
 * RELATED:
 *   - apps/<tenant>/src/features/kanban/allocation/AllocationWhyPopover.tsx: re-export shim
 *   - packages/email-hub/src/_stubs/kanban/*: email-hub side
 *     mirrors that depend on these symbols.
 */

import { useEffect, useMemo, useRef } from 'react'
import { Brain, ChevronDown, Clock, History, MapPin, X } from 'lucide-react'

import { cn } from '@quotech/tenant-shared/lib/utils'

import type { KanbanApi } from '@quotech/ui/kanban'

import { useAllocationExplain } from '../index'
import {
  buildWherePath,
  decisionReasoningTitle,
  describeAuditEvent,
  extractTopCandidates,
  formatConfidencePill,
} from '../index'

export interface AllocationWhyPopoverProps {
  api: KanbanApi
  allocationId: string
  /** When false the component renders nothing AND does not fetch. */
  open: boolean
  onClose: () => void
  /** Anchor position relative to the viewport (right-click coords). */
  x: number
  y: number
}

// ─────────────────────────────────────────────────────────
// Component (pure I → O helpers live in ./AllocationWhyPopover.helpers.ts)
// ─────────────────────────────────────────────────────────

export function AllocationWhyPopover({
  api,
  allocationId,
  open,
  onClose,
  x,
  y,
}: AllocationWhyPopoverProps) {
  const ref = useRef<HTMLDivElement>(null)
  const { data, isLoading, isError, error, refetch } = useAllocationExplain({
    api,
    allocationId,
    enabled: open,
  })

  // Outside click + Esc to close. We intentionally NOT use
  // `pointerdown` (matches the chip dropdown's `mousedown` so the
  // popover doesn't fight the chip's own dismiss handler).
  useEffect(() => {
    if (!open) return
    const onAway = (e: MouseEvent) => {
      if (!ref.current) return
      if (!ref.current.contains(e.target as Node)) onClose()
    }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('mousedown', onAway)
    window.addEventListener('keydown', onKey)
    return () => {
      window.removeEventListener('mousedown', onAway)
      window.removeEventListener('keydown', onKey)
    }
  }, [open, onClose])

  const topCandidates = useMemo(
    () =>
      data?.classification_snapshot
        ? extractTopCandidates(data.classification_snapshot)
        : [],
    [data?.classification_snapshot],
  )

  const confidencePill = useMemo(
    () =>
      data
        ? formatConfidencePill(data.confidence, data.decision_type)
        : null,
    [data],
  )

  const reasoningTitle = useMemo(
    () => decisionReasoningTitle(data?.decision_type),
    [data?.decision_type],
  )

  const evidenceReason =
    data?.evidence && typeof data.evidence['reason'] === 'string'
      ? String(data.evidence['reason']).trim()
      : ''

  const altCandidates = useMemo(() => {
    if (data?.alternatives && data.alternatives.length > 0) {
      return extractTopCandidates({
        top_candidates: data.alternatives as unknown[],
      })
    }
    return topCandidates
  }, [data?.alternatives, topCandidates])

  if (!open) return null

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Allocation explanation"
      className="fixed z-[2147483647] min-w-[280px] max-w-[360px] rounded-md border border-border bg-card shadow-xl p-2 text-[11px]"
      style={{ top: y, left: x }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-1 pb-1.5 border-b border-border/60">
        <div className="flex items-center gap-1 text-foreground/80 font-semibold">
          <Brain className="h-3.5 w-3.5 text-amber-500" />
          Why this board?
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="text-muted-foreground hover:text-foreground"
        >
          <X className="h-3 w-3" />
        </button>
      </div>

      {isLoading && (
        <div className="px-2 py-3 text-muted-foreground">Loading…</div>
      )}

      {isError && (
        <div className="px-2 py-3 text-red-500">
          Couldn't load explainer{error?.message ? `: ${error.message}` : ''}.
          <button
            type="button"
            onClick={refetch}
            className="ml-2 underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {data && (
        <div className="space-y-2 py-1">
          {/* Where — FEAT-2026-07-15-EXPLAIN-BOARD-CONTEXT: the
              operator couldn't tell whether the lane belonged to a
              sub-board ("is it part of the Inbox board?"). Render the
              full Parent › Board › Lane path + board kind up front. */}
          {buildWherePath(data) && (
            <Section icon={MapPin} title="Where">
              <div className="text-foreground">{buildWherePath(data)}</div>
              {data.board_kind && (
                <div className="text-muted-foreground/80">
                  {data.parent_board_name
                    ? `Sub-board of ${data.parent_board_name}`
                    : `${String(data.board_kind).toLowerCase()} board`}
                </div>
              )}
            </Section>
          )}

          {/* Phase 4 — board purpose only when cited (is_authoritative). */}
          {data.board_dossier?.is_authoritative &&
            data.board_dossier.purpose_text.trim() !== '' && (
            <Section icon={Brain} title="Board purpose">
              <div className="whitespace-pre-wrap text-foreground">
                {data.board_dossier.purpose_text}
              </div>
              <div className="text-muted-foreground/80">
                Cited from {data.board_dossier.cited_correction_count} accepted
                correction
                {data.board_dossier.cited_correction_count === 1 ? '' : 's'}
              </div>
            </Section>
          )}

          {/* Snapshot — RT-03: title follows decision_type (not always AI).
              Placement Why text is evidence.reason only (KeyVal below);
              never synthesize from snapshot / email AI summary. */}
          <Section icon={Brain} title={reasoningTitle}>
            {data.decision_type && (
              <KeyVal
                k="Decision"
                v={String(data.decision_type).replaceAll('_', ' ')}
              />
            )}
            <KeyVal
              k="Source"
              v={data.source.replaceAll('_', ' ').toLowerCase()}
            />
            {evidenceReason && (
              <KeyVal k="Evidence" v={evidenceReason} />
            )}
            {data.evidence &&
              data.evidence['legacy_evidence_unavailable'] === true && (
              <KeyVal
                k="Evidence"
                v="No persisted decision journal for this placement"
              />
            )}
            {data.evidence &&
              typeof data.evidence['journal_id'] === 'number' && (
              <KeyVal k="Journal" v={`#${data.evidence['journal_id']}`} />
            )}
            {confidencePill && (
              <KeyVal
                k="Confidence"
                v={
                  <span
                    className={cn(
                      'px-1 rounded',
                      confidencePill.tone === 'green' &&
                        'bg-green-100 text-green-800 dark:bg-green-950/40 dark:text-green-200',
                      confidencePill.tone === 'amber' &&
                        'bg-amber-100 text-amber-800 dark:bg-amber-950/40 dark:text-amber-200',
                      confidencePill.tone === 'muted' &&
                        'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300',
                    )}
                  >
                    {confidencePill.label}
                  </span>
                }
              />
            )}
            {data.decision_type === 'model' &&
              typeof data.classification_snapshot['model_id'] === 'string' && (
              <KeyVal
                k="Model"
                v={String(data.classification_snapshot['model_id'])}
              />
            )}
            {data.decision_type === 'model' &&
              typeof data.classification_snapshot['prompt_version'] ===
              'string' && (
              <KeyVal
                k="Prompt"
                v={String(data.classification_snapshot['prompt_version'])}
              />
            )}
            {altCandidates.length > 0 && (
              <div className="mt-1">
                <div className="text-muted-foreground/80 mb-0.5">
                  Top candidates:
                </div>
                <ul className="space-y-0.5">
                  {altCandidates.slice(0, 3).map((c) => (
                    <li
                      key={c.label}
                      className="flex items-center justify-between"
                    >
                      <span className="truncate">{c.label}</span>
                      {c.score != null && (
                        <span className="text-muted-foreground tabular-nums">
                          {Math.round(c.score * 100)}%
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </Section>

          {/* History */}
          <Section icon={History} title="History">
            <KeyVal
              k="Corrections for this email"
              v={String(data.prior_correction_count_for_email)}
            />
            <KeyVal
              k="Sender → board pattern"
              v={`${data.prior_correction_count_for_sender_board_pair} prior correction(s)`}
            />
            {data.superseded_by_count > 0 && (
              <KeyVal
                k="Superseded"
                v={`${data.superseded_by_count} time(s)`}
              />
            )}
          </Section>

          {/* Audit excerpt — only render when we actually have entries */}
          {data.audit_log_excerpt && data.audit_log_excerpt.length > 0 && (
            <Section icon={Clock} title="Lifecycle">
              <ul className="space-y-0.5 max-h-24 overflow-y-auto">
                {data.audit_log_excerpt.map((entry, idx) => (
                  <li
                    key={
                      typeof entry['at'] === 'string'
                        ? `${String(entry['at'])}-${idx}`
                        : idx
                    }
                    className="flex items-start gap-2"
                  >
                    <ChevronDown className="h-2.5 w-2.5 mt-1 shrink-0 text-muted-foreground/60" />
                    <div className="flex-1 min-w-0">
                      <div className="truncate">{describeAuditEvent(entry)}</div>
                      {typeof entry['at'] === 'string' && (
                        <div className="text-[9px] text-muted-foreground/70">
                          {String(entry['at'])}
                        </div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
        </div>
      )}
    </div>
  )
}

// ─────────────────────────────────────────────────────────
// Private layout primitives
// ─────────────────────────────────────────────────────────

function Section({
  icon: Icon,
  title,
  children,
}: {
  icon: typeof Brain
  title: string
  children: React.ReactNode
}) {
  return (
    <div>
      <div className="flex items-center gap-1 px-1 mb-1 text-muted-foreground/80 uppercase tracking-wide font-semibold text-[10px]">
        <Icon className="h-2.5 w-2.5" />
        {title}
      </div>
      <div className="px-1 space-y-0.5">{children}</div>
    </div>
  )
}

function KeyVal({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-muted-foreground/80 truncate">{k}</span>
      <span className="text-foreground truncate text-right">{v}</span>
    </div>
  )
}
